import { useSuspenseQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { useTranslation } from '@/lib/i18n'
import { CallCorrections } from '../-components/call-corrections'
import { CallData } from '../-components/call-data'
import { CallSummary, PublishBar } from '../-components/call-summary'
import { DatesStep } from '../-components/dates-step'
import { MailboxTestStep } from '../-components/mailbox-test-step'
import { ResidenceStep } from '../-components/residence-step'
import { StepList } from '../-components/step'
import { useWorkingCall } from '../-hooks/use-working-call'
import { managedCallQueryOptions } from '../-query/query-options'

export const Route = createFileRoute('/(private)/bando/$residenceId/')({
	component: CallPage
})

/**
 * The call of one residence (FFD 2.2), read top to bottom in the order of the
 * work. A draft opens on its three steps (dates, test PEC, the data check)
 * and a published call on its data; the photos live on the residence page.
 * On the right a card follows the page: where the
 * call stands, "Pubblica", the figures and the notice to download. The state
 * sits beside the residence name in the breadcrumb.
 */
function CallPage() {
	const { t } = useTranslation()
	const { residenceId } = Route.useParams()
	const { callId } = useWorkingCall()
	const myCall = useSuspenseQuery(
		managedCallQueryOptions(residenceId, callId)
	).data
	const { residence } = myCall

	return (
		<main className='@container flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto p-4 md:p-6'>
			<h1 className='sr-only'>{residence.name}</h1>
			<div className='grid @4xl:grid-cols-[minmax(0,1fr)_20rem] items-start gap-6'>
				<aside className='@4xl:sticky @4xl:top-0 @4xl:order-last'>
					<CallSummary myCall={myCall} />
				</aside>
				<div className='flex min-w-0 flex-col gap-8'>
					{myCall.state === 'draft' ? (
						<StepList>
							<DatesStep myCall={myCall} number={1} />
							<MailboxTestStep myCall={myCall} number={2} />
							<ResidenceStep myCall={myCall} number={3} />
						</StepList>
					) : (
						<>
							{myCall.canChangeDates && (
								<StepList>
									<DatesStep myCall={myCall} />
								</StepList>
							)}
							<section
								aria-labelledby='residence-data-title'
								className='flex flex-col gap-3'
							>
								<h2
									className='font-semibold text-base'
									id='residence-data-title'
								>
									{t('admin-call:residence.title')}
								</h2>
								<CallData residence={residence} />
							</section>
							{!myCall.readOnly && (
								<CallCorrections myCall={myCall} />
							)}
						</>
					)}
				</div>
			</div>
			<PublishBar myCall={myCall} />
		</main>
	)
}
